import { ReactElement } from 'react';
import { Trans } from 'react-i18next';

import {
   Logo,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   StrongText,
   StyledFlex,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';
import { Translate, useBoolean, useTranslateDeclension } from '@dreryk/react-common';

import { getLoginSettingsPath } from '~legacy.personnel';

import { mfaRequiredTranslationKeys } from '../../translations';

import { getDaysLeftCount } from './MfaRequiredModal.utils';
import { MfaRequiredHotlineLink } from './MfaRequiredHotlineLink';
import { MfaRequiredEmailLink } from './MfaRequiredEmailLink';
import { MfaRequiredModalLoader } from './MfaRequiredModal.loader';
import { MfaRequiredHelpCenterLink } from './MfaRequiredHelpCenterLink';
import { SHOULD_SHOW_MODAL_STORAGE_KEY } from './MfaRequired.settings';

// TODO DREGAB-27686 Temporary component, to be removed after '2026-05-07'
// eslint-disable-next-line max-lines-per-function
const MfaRequiredModalBase = (): ReactElement | null => {
   const [{ setFalse: closeModal }, showModal] = useBoolean(true);
   const shouldShowModal = (): string | null => window.sessionStorage.getItem(SHOULD_SHOW_MODAL_STORAGE_KEY);

   const push = useHistoryPushWithBackAction(false);
   const handleConfirm = (): void => {
      push(`${getLoginSettingsPath()}?showMfa=true`, {});
      closeModal();
   };

   const handleClose = (): void => {
      closeModal();
      window.sessionStorage.setItem(SHOULD_SHOW_MODAL_STORAGE_KEY, 'false');
   };

   const daysCountSuffix = useTranslateDeclension(
      getDaysLeftCount(),
      mfaRequiredTranslationKeys.day,
      mfaRequiredTranslationKeys.days,
      mfaRequiredTranslationKeys.days
   );

   if (!showModal || shouldShowModal() === 'false') {
      return null;
   }

   return (
      <Modal
         testId={'mfaRequired'}
         title={mfaRequiredTranslationKeys.improvingAccountSecurity}
         width={ModalWidth.WIDE}
         onCloseRequest={handleClose}
      >
         <StyledFlex direction={'column'} gap={'24px'}>
            <StyledFlex direction={'column'} gap={'8px'}>
               <Translate>{mfaRequiredTranslationKeys.mfaWillBeRequiredSoon}</Translate>
               <span>
                  <Trans
                     i18nKey={mfaRequiredTranslationKeys.youHaveXDaysLeft}
                     components={{ strong: <StrongText testId={'dayCount'} /> }}
                     values={{ daysCount: getDaysLeftCount(), daysCountSuffix: daysCountSuffix }}
                  />
               </span>
               <Translate>{mfaRequiredTranslationKeys.youWontBeAbleToAccessAccountAfterDate}</Translate>
               <Translate>{mfaRequiredTranslationKeys.mfaIsAboutUsingApp}</Translate>
               <ul>
                  <li>
                     <Translate>{mfaRequiredTranslationKeys.googleAuthenticator}</Translate>
                  </li>
                  <li>
                     <Translate>{mfaRequiredTranslationKeys.msAuthenticator}</Translate>
                  </li>
               </ul>
            </StyledFlex>
            <StyledFlex direction={'column'} gap={'8px'}>
               <Translate>{mfaRequiredTranslationKeys.doYouNeedHelpConfiguringMfa}</Translate>
               <span>
                  <Trans
                     i18nKey={mfaRequiredTranslationKeys.hotline}
                     components={{
                        link: <MfaRequiredHotlineLink />,
                     }}
                  />
               </span>
               <span>
                  <Trans
                     i18nKey={mfaRequiredTranslationKeys.email}
                     components={{
                        link: <MfaRequiredEmailLink />,
                     }}
                  />
               </span>
               <span>
                  <Trans
                     i18nKey={mfaRequiredTranslationKeys.helpCenter}
                     components={{
                        link: <MfaRequiredHelpCenterLink />,
                     }}
                  />
               </span>
            </StyledFlex>
            <Logo testId={'drErykLogo'} />
         </StyledFlex>
         <ModalConfirmButton label={mfaRequiredTranslationKeys.activeMfaNow} onClick={handleConfirm} />
         <ModalCloseButton onClick={handleClose} />
         <ModalCancelButton onClick={handleClose} label={mfaRequiredTranslationKeys.remindLater} />
      </Modal>
   );
};

export const MfaRequiredModal = (): ReactElement | null => {
   return (
      <MfaRequiredModalLoader>
         <MfaRequiredModalBase />
      </MfaRequiredModalLoader>
   );
};
